import React, {useEffect, useRef} from 'react';
import {Col, Row} from 'react-bootstrap';
import {DateTime} from 'luxon';
import echarts from '@/utils/echartsCore';
import i18n from 'i18n';

export interface UserAuditLoginsSessionsChartsProps {
    dayKeys: string[];
    axisLabels: string[];
    loginCounts: number[];
    sessionCounts: number[];
    totalSessionDurationSeconds: number[];
    avgSessionDurationSeconds: number[];
}

const CHART_HEIGHT_PX = 260;
const TARGET_AXIS_LABELS = 12;

function tooltipDayTitle(dayKey: string): string {
    try {
        return DateTime.fromISO(dayKey, {zone: 'utc'}).toLocaleString(DateTime.DATE_MED);
    } catch {
        return dayKey;
    }
}

function formatDurationSeconds(sec: number): string {
    if (sec == null || Number.isNaN(sec) || sec < 60) {
        return `${Math.round(sec || 0)}s`;
    }
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return `${m}m ${s}s`;
}

const UserAuditLoginsSessionsCharts: React.FC<UserAuditLoginsSessionsChartsProps> = ({
    dayKeys,
    axisLabels,
    loginCounts,
    sessionCounts,
    totalSessionDurationSeconds,
    avgSessionDurationSeconds,
}) => {
    const loginRef = useRef<HTMLDivElement>(null);
    const sessionsRef = useRef<HTMLDivElement>(null);
    const totalDurRef = useRef<HTMLDivElement>(null);
    const avgDurRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const loginEl = loginRef.current;
        const sessionsEl = sessionsRef.current;
        const totalEl = totalDurRef.current;
        const avgEl = avgDurRef.current;
        if (!loginEl || !sessionsEl || !totalEl || !avgEl) {
            return;
        }

        const totalMinutes = totalSessionDurationSeconds.map((s) => Math.round((s / 60) * 100) / 100);
        const avgMinutes = avgSessionDurationSeconds.map((s) => Math.round((s / 60) * 100) / 100);

        const loginChart = echarts.init(loginEl);
        const sessionsChart = echarts.init(sessionsEl);
        const totalChart = echarts.init(totalEl);
        const avgChart = echarts.init(avgEl);

        const handleResize = () => {
            [loginChart, sessionsChart, totalChart, avgChart].forEach((c) => {
                if (!c.isDisposed()) {
                    c.resize();
                }
            });
        };

        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(loginEl);
            resizeObserver.observe(sessionsEl);
            resizeObserver.observe(totalEl);
            resizeObserver.observe(avgEl);
        }
        window.addEventListener('resize', handleResize);

        const dayCount = axisLabels.length;
        const labelInterval = Math.max(0, Math.ceil(dayCount / TARGET_AXIS_LABELS) - 1);
        const rotate = dayCount > 10 ? 28 : 0;

        const axisLabel = {
            rotate,
            interval: labelInterval,
            hideOverlap: true,
        };

        const tooltipCounts = {
            trigger: 'axis' as const,
            axisPointer: {type: 'shadow' as const},
            formatter: (params: Array<{dataIndex: number; marker: string; seriesName: string; value: number}>) => {
                if (!params?.length) {
                    return '';
                }
                const idx = params[0].dataIndex;
                const title = tooltipDayTitle(dayKeys[idx] ?? '');
                const lines = params.map(
                    (p) => `${p.marker} ${p.seriesName}: <strong>${p.value}</strong>`,
                );
                return `${title}<br/>${lines.join('<br/>')}`;
            },
        };

        const tooltipMinutes = {
            trigger: 'axis' as const,
            axisPointer: {type: 'shadow' as const},
            formatter: (params: Array<{dataIndex: number; marker: string; seriesName: string; value: number}>) => {
                if (!params?.length) {
                    return '';
                }
                const idx = params[0].dataIndex;
                const title = tooltipDayTitle(dayKeys[idx] ?? '');
                const p = params[0];
                const sec = Number(p.value) * 60;
                const human = formatDurationSeconds(sec);
                return `${title}<br/>${p.marker} ${p.seriesName}: <strong>${human}</strong>`;
            },
        };

        const minuteAxis = {
            type: 'value' as const,
            axisLabel: {
                formatter: (v: number) => (v >= 60 ? `${(v / 60).toFixed(1)}h` : `${v}m`),
            },
        };

        loginChart.setOption(
            {
                textStyle: {fontFamily: '"Rubik", sans-serif', fontSize: 12},
                tooltip: tooltipCounts,
                grid: {left: 44, right: 12, top: 12, bottom: rotate ? 48 : 24},
                xAxis: {
                    type: 'category',
                    data: axisLabels,
                    axisLabel,
                },
                yAxis: {type: 'value', minInterval: 1},
                series: [
                    {
                        name: i18n.t('ub_chart_logins_series.text'),
                        type: 'bar',
                        data: loginCounts,
                    },
                ],
            },
            true,
        );

        sessionsChart.setOption(
            {
                textStyle: {fontFamily: '"Rubik", sans-serif', fontSize: 12},
                tooltip: tooltipCounts,
                grid: {left: 44, right: 12, top: 12, bottom: rotate ? 48 : 24},
                xAxis: {
                    type: 'category',
                    data: axisLabels,
                    axisLabel,
                },
                yAxis: {type: 'value', minInterval: 1},
                series: [
                    {
                        name: i18n.t('ub_chart_sessions_series.text'),
                        type: 'bar',
                        data: sessionCounts,
                    },
                ],
            },
            true,
        );

        totalChart.setOption(
            {
                textStyle: {fontFamily: '"Rubik", sans-serif', fontSize: 12},
                tooltip: tooltipMinutes,
                grid: {left: 52, right: 12, top: 12, bottom: rotate ? 48 : 24},
                xAxis: {
                    type: 'category',
                    data: axisLabels,
                    axisLabel,
                },
                yAxis: minuteAxis,
                series: [
                    {
                        name: i18n.t('ub_chart_total_session_time_series.text'),
                        type: 'bar',
                        data: totalMinutes,
                    },
                ],
            },
            true,
        );

        avgChart.setOption(
            {
                textStyle: {fontFamily: '"Rubik", sans-serif', fontSize: 12},
                tooltip: tooltipMinutes,
                grid: {left: 52, right: 12, top: 12, bottom: rotate ? 48 : 24},
                xAxis: {
                    type: 'category',
                    data: axisLabels,
                    axisLabel,
                },
                yAxis: minuteAxis,
                series: [
                    {
                        name: i18n.t('ub_chart_avg_session_length_series.text'),
                        type: 'bar',
                        data: avgMinutes,
                    },
                ],
            },
            true,
        );

        return () => {
            window.removeEventListener('resize', handleResize);
            resizeObserver?.disconnect();
            loginChart.dispose();
            sessionsChart.dispose();
            totalChart.dispose();
            avgChart.dispose();
        };
    }, [dayKeys, axisLabels, loginCounts, sessionCounts, totalSessionDurationSeconds, avgSessionDurationSeconds]);

    return (
        <div className="mb-3">
            <Row className="g-3 mb-1">
                <Col md={6}>
                    <h6 className="mb-2">{i18n.t('ub_chart_logins_per_day.text')}</h6>
                    <div ref={loginRef} style={{height: CHART_HEIGHT_PX, width: '100%'}} />
                </Col>
                <Col md={6}>
                    <h6 className="mb-2">{i18n.t('ub_chart_sessions_per_day.text')}</h6>
                    <div ref={sessionsRef} style={{height: CHART_HEIGHT_PX, width: '100%'}} />
                </Col>
            </Row>
            <Row className="g-3">
                <Col md={6}>
                    <h6 className="mb-2">{i18n.t('ub_chart_total_session_time_per_day.text')}</h6>
                    <div ref={totalDurRef} style={{height: CHART_HEIGHT_PX, width: '100%'}} />
                </Col>
                <Col md={6}>
                    <h6 className="mb-2">{i18n.t('ub_chart_avg_session_length_per_day.text')}</h6>
                    <div ref={avgDurRef} style={{height: CHART_HEIGHT_PX, width: '100%'}} />
                </Col>
            </Row>
        </div>
    );
};

export default UserAuditLoginsSessionsCharts;
